import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HiOutlineArrowTopRightOnSquare } from 'react-icons/hi2';
import { usePortfolios, useSettings } from '../hooks/useApi';
import { useLanguage } from '../context/LanguageContext';
import Footer from '../components/Footer';

const ITEMS_PER_PAGE = 18;

const cardVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } },
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.25 } },
};

const SERVER_BASE = (process.env.REACT_APP_API_URL || '/api').replace(/\/api\/?$/, '');
const getImageUrl = (item) => {
  const img = item.thumbnail || item.image;
  if (!img) return null;
  if (img.startsWith('http')) return img;
  return `${SERVER_BASE}${img}`;
};

const PLACEHOLDER_COLORS = [
  'linear-gradient(135deg, #1a2a2a, #0f3a36)',
  'linear-gradient(135deg, #1a1a2a, #0f1a3a)',
  'linear-gradient(135deg, #2a1a2a, #3a0f2a)',
  'linear-gradient(135deg, #2a2a1a, #3a360f)',
  'linear-gradient(135deg, #1a2a1a, #0f3a1a)',
  'linear-gradient(135deg, #2a1a1a, #3a1a0f)',
];

function PortfolioListPage() {
  const { t, getField } = useLanguage();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('All');
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const { data: portfolioData, loading } = usePortfolios({ limit: 200, portfolioOnly: true });
  const { data: settings } = useSettings();

  const allItems = Array.isArray(portfolioData) ? portfolioData :
    portfolioData?.data || [];

  const yearFilters = React.useMemo(() => {
    const years = [...new Set(allItems.map(item => String(item.year)))].sort((a, b) => b - a);
    return ['All', ...years];
  }, [allItems]);

  const filteredItems = activeFilter === 'All'
    ? allItems
    : allItems.filter(item => String(item.year) === activeFilter);

  const displayedItems = filteredItems.slice(0, visibleCount);
  const hasMore = visibleCount < filteredItems.length;

  const handleFilterChange = useCallback((filter) => {
    setActiveFilter(filter);
    setVisibleCount(ITEMS_PER_PAGE);
  }, []);

  const loadMore = () => {
    setVisibleCount(prev => prev + ITEMS_PER_PAGE);
  };

  if (loading) {
    return (
      <main>
        <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="spinner" />
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main>
      <section className="section portfolio" style={{ paddingTop: '120px' }}>
        <div className="container">
          <motion.div
            className="section__header"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section__label">{t('portfolio.label')}</span>
            <h2 className="section__title">{settings?.portfolio_title || t('portfolio.title')}</h2>
            <div className="divider" />
            <p className="section__subtitle">
              {settings?.portfolio_subtitle || t('portfolio.subtitle')}
            </p>
          </motion.div>

          {/* Year Filters */}
          <motion.div
            className="portfolio__filters"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {yearFilters.map((filter) => (
              <button
                key={filter}
                className={`portfolio__filter-btn ${activeFilter === filter ? 'portfolio__filter-btn--active' : ''}`}
                onClick={() => handleFilterChange(filter)}
              >
                {filter === 'All' ? t('portfolio.all') : filter}
              </button>
            ))}
          </motion.div>

          {/* Grid */}
          <div className="portfolio__grid">
            <AnimatePresence mode="popLayout">
              {displayedItems.map((item, idx) => (
                <motion.div
                  key={item._id || idx}
                  className="portfolio__card"
                  variants={cardVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  layout
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/portfolio/${item._id}`)}
                >
                  {getImageUrl(item) ? (
                    <img
                      className="portfolio__card-image"
                      src={getImageUrl(item)}
                      alt={getField(item, 'title')}
                      loading="lazy"
                    />
                  ) : (
                    <div
                      className="portfolio__card-placeholder"
                      style={{ background: PLACEHOLDER_COLORS[idx % PLACEHOLDER_COLORS.length] }}
                    >
                      <HiOutlineArrowTopRightOnSquare />
                    </div>
                  )}
                  <div className="portfolio__card-overlay">
                    <span className="portfolio__card-category">{item.category}</span>
                    <h3 className="portfolio__card-title">{getField(item, 'title')}</h3>
                    <span className="portfolio__card-year">{item.year}</span>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Load More */}
          {hasMore && (
            <div className="portfolio__load-more">
              <button className="btn btn--outline" onClick={loadMore}>
                {t('portfolio.loadMore')}
              </button>
            </div>
          )}

          {filteredItems.length === 0 && (
            <p style={{ textAlign: 'center', color: '#777', marginTop: 40 }}>
              {t('portfolio.empty')}
            </p>
          )}
        </div>
      </section>
      <Footer />
    </main>
  );
}

export default PortfolioListPage;
